import React, { useEffect, useState, useReducer, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../lib/apiClient';
import { Loader2, Briefcase } from 'lucide-react';
import JobFilters from './JobFilters';
import { jobMatchesFilter } from '../../utils/jobMatching.js';
import { DEFAULT_FILTERS, hasActiveCriteria } from '../../constants/defaultFilters.js';
import { JobListHeader } from './JobsFilter/JobListHeader.jsx';
import { JobCard } from './JobsFilter/JobCard.jsx';
import { PaginationFooter } from './JobsFilter/PaginationFooter.jsx';

const sortFilters = (list) => list.toSorted((a, b) => {
    const ap = a.is_pinned ? 1 : 0;
    const bp = b.is_pinned ? 1 : 0;
    if (ap !== bp) return bp - ap;
    return (a.name || '').localeCompare(b.name || '');
});

// --- Job fetch lifecycle (jobs / loading / error move together) -------------
const jobsInitialState = { jobs: [], loading: true, error: null };
function jobsReducer(state, action) {
    switch (action.type) {
        case 'FETCH_START':
            return { ...state, loading: true, error: null };
        case 'FETCH_SUCCESS':
            return { ...state, loading: false, jobs: action.jobs };
        case 'FETCH_ERROR':
            return { ...state, loading: false, error: action.error };
        default:
            return state;
    }
}

// --- Pagination (page + page size; size change resets to page 1) ------------
const paginationInitialState = { currentPage: 1, itemsPerPage: 20 };
function paginationReducer(state, action) {
    switch (action.type) {
        case 'RESET_PAGE':
            return { ...state, currentPage: 1 };
        case 'SET_ITEMS_PER_PAGE':
            return { ...state, itemsPerPage: action.itemsPerPage, currentPage: 1 };
        case 'PREV_PAGE':
            return { ...state, currentPage: Math.max(1, state.currentPage - 1) };
        case 'NEXT_PAGE':
            return { ...state, currentPage: Math.min(action.totalPages, state.currentPage + 1) };
        default:
            return state;
    }
}

// --- Saved-filter management (the list + active-edit name + save indicator) --
const filterMetaInitialState = {
    savedFiltersList: [],
    editingFilterName: null,
    isSavingFilter: false,
};
function filterMetaReducer(state, action) {
    switch (action.type) {
        case 'SET_LIST':
            return { ...state, savedFiltersList: action.savedFiltersList };
        case 'SET_EDITING_NAME':
            return { ...state, editingFilterName: action.editingFilterName };
        case 'SET_SAVING':
            return { ...state, isSavingFilter: action.isSavingFilter };
        default:
            return state;
    }
}

export default function JobsFilterView({ onViewChange, activeFilterId }) {
    const { user } = useAuth();
    const [{ jobs, loading, error }, dispatchJobs] = useReducer(jobsReducer, jobsInitialState);
    const [expandedJobId, setExpandedJobId] = useState(null);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);

    // Pagination State
    const [{ currentPage, itemsPerPage }, dispatchPagination] = useReducer(paginationReducer, paginationInitialState);

    // Setup / Edit Mode State + saved-filter list
    const [{ savedFiltersList, editingFilterName, isSavingFilter }, dispatchFilterMeta] = useReducer(
        filterMetaReducer,
        filterMetaInitialState
    );

    // Fetch All Filters & Set Active
    const fetchAllFilters = useCallback(async () => {
        if (!user) return;

        try {
            const data = await api.getFilters();
            const list = sortFilters(data.filters || []);
            dispatchFilterMeta({ type: 'SET_LIST', savedFiltersList: list });

            if (activeFilterId) {
                const active = list.find(f => f.id === activeFilterId);
                if (active) {
                    dispatchFilterMeta({ type: 'SET_EDITING_NAME', editingFilterName: active.name });
                    if (active.criteria && Object.keys(active.criteria).length > 0) {
                        setFilters({ ...DEFAULT_FILTERS, ...active.criteria });
                    } else {
                        setFilters(DEFAULT_FILTERS);
                    }
                }
            } else {
                dispatchFilterMeta({ type: 'SET_EDITING_NAME', editingFilterName: null });
                setFilters(DEFAULT_FILTERS);
            }
        } catch (err) {
            console.error('Error fetching filters:', err);
        }
    }, [user, activeFilterId]);

    useEffect(() => {
        fetchAllFilters();
    }, [fetchAllFilters]);

    const handleTogglePin = async (filterId, isPinned) => {
        // Optimistic Update
        dispatchFilterMeta({
            type: 'SET_LIST',
            savedFiltersList: sortFilters(savedFiltersList.map(f =>
                f.id === filterId ? { ...f, is_pinned: isPinned } : f
            )),
        });

        try {
            await api.updateFilter(filterId, { is_pinned: isPinned });
        } catch (err) {
            console.error('Error updating pin:', err);
            fetchAllFilters();
        }
    };

    // Auto-Save Logic (Debounced)
    useEffect(() => {
        if (!activeFilterId || !user) return;

        const saveTimer = setTimeout(async () => {
            dispatchFilterMeta({ type: 'SET_SAVING', isSavingFilter: true });
            try {
                await api.updateFilter(activeFilterId, {
                    criteria: filters,
                    status: 'Active'
                });
            } catch (err) {
                console.error('Auto-save failed:', err);
            } finally {
                dispatchFilterMeta({ type: 'SET_SAVING', isSavingFilter: false });
            }
        }, 1000);

        return () => clearTimeout(saveTimer);
    }, [filters, activeFilterId, user]);

    const fetchJobs = useCallback(async () => {
        dispatchJobs({ type: 'FETCH_START' });
        try {
            const data = await api.getJobs({ limit: 500 });
            const allJobs = data.jobs || [];
            const filtered = allJobs.filter(job => jobMatchesFilter(job, filters));
            dispatchJobs({ type: 'FETCH_SUCCESS', jobs: filtered });
        } catch (err) {
            console.error('Error fetching jobs:', err);
            dispatchJobs({ type: 'FETCH_ERROR', error: err.message });
        }
    }, [filters]);

    // Debounce fetch when filters change
    useEffect(() => {
        dispatchPagination({ type: 'RESET_PAGE' });
        const timer = setTimeout(() => {
            fetchJobs();
        }, 500);
        return () => clearTimeout(timer);
    }, [fetchJobs]);

    const handleRenameFilter = async () => {
        if (!editingFilterName) return;

        const newName = prompt('Enter new filter name:', editingFilterName);
        if (newName && newName.trim() !== '' && newName !== editingFilterName) {
            try {
                await api.updateFilter(activeFilterId, { name: newName });
                dispatchFilterMeta({ type: 'SET_EDITING_NAME', editingFilterName: newName });
                dispatchFilterMeta({
                    type: 'SET_LIST',
                    savedFiltersList: sortFilters(savedFiltersList.map(f =>
                        f.id === activeFilterId ? { ...f, name: newName } : f
                    )),
                });
            } catch (err) {
                console.error('Error renaming filter:', err);
                alert('Failed to rename filter');
            }
        }
    };

    // "Save as filter" from the live (unsaved) view: persist the current tweaks
    // as a brand-new saved filter and switch into editing it (auto-save takes
    // over from there). Only offered when no saved filter is active.
    const handleSaveAsFilter = async () => {
        const name = prompt('Name this filter:', 'My filter');
        if (!name || !name.trim()) return;

        try {
            const data = await api.createFilter({
                name: name.trim(),
                criteria: filters,
                notifications: false,
                status: 'Active'
            });
            dispatchFilterMeta({
                type: 'SET_LIST',
                savedFiltersList: sortFilters([...savedFiltersList, { ...data, is_pinned: false }]),
            });
            if (data?.id) onViewChange('jobs', data.id);
        } catch (err) {
            console.error('Error saving filter:', err);
            alert('Failed to save filter');
        }
    };

    const handleDuplicateFilter = async () => {
        const activeFilter = savedFiltersList.find(f => f.id === activeFilterId);
        if (!activeFilter) return;

        try {
            const data = await api.createFilter({
                name: `${activeFilter.name} (Copy)`,
                criteria: activeFilter.criteria,
                notifications: false,
                status: 'Not Active'
            });

            dispatchFilterMeta({
                type: 'SET_LIST',
                savedFiltersList: sortFilters([...savedFiltersList, { ...data, is_pinned: false }]),
            });
            if (data?.id) onViewChange('jobs', data.id);
            alert('Filter duplicated!');
        } catch (err) {
            console.error('Error duplicating filter:', err);
            alert('Failed to duplicate filter');
        }
    };

    const handleDeleteFilter = async () => {
        if (!confirm('Are you sure you want to delete this filter?')) return;

        try {
            await api.deleteFilter(activeFilterId);
            const remainingFilters = savedFiltersList.filter(f => f.id !== activeFilterId);
            dispatchFilterMeta({ type: 'SET_LIST', savedFiltersList: remainingFilters });

            const topPinned = remainingFilters.find(f => f.is_pinned);
            if (topPinned) {
                onViewChange('jobs', topPinned.id);
            } else {
                onViewChange('jobs');
            }
        } catch (err) {
            console.error('Error deleting filter:', err);
            alert('Failed to delete filter');
        }
    };

    return (
        <div className="p-8 max-w-[95%] mx-auto">
            <JobListHeader
                savedFiltersList={savedFiltersList}
                activeFilterId={activeFilterId}
                editingFilterName={editingFilterName}
                isSavingFilter={isSavingFilter}
                onSelect={(id) => onViewChange('jobs', id)}
                onCreate={() => onViewChange('saved-filters')}
                onTogglePin={handleTogglePin}
                onRename={handleRenameFilter}
                onDuplicate={handleDuplicateFilter}
                onDelete={handleDeleteFilter}
                canSaveAsFilter={!activeFilterId && hasActiveCriteria(filters)}
                onSaveAsFilter={handleSaveAsFilter}
            />

            <div className="flex flex-col md:flex-row gap-8 items-start">

                {/* Filters Sidebar */}
                <JobFilters filters={filters} onChange={setFilters} />

                {/* Job List Table */}
                <div className="flex-1 min-w-0 flex flex-col min-h-[500px] relative bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    {loading && (
                        <div className="absolute inset-0 bg-white/80 z-10 flex items-start justify-center pt-20">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    )}

                    {error && (
                        <div className="p-8 text-center text-red-600 bg-red-50 m-4 rounded-xl border border-red-100">
                            <p>Error loading jobs: {error}</p>
                            <button type="button" onClick={fetchJobs} className="mt-2 text-sm underline hover:text-red-800">Retry</button>
                        </div>
                    )}

                    {!loading && !error && jobs.length === 0 && (
                        <div className="text-center py-12 text-gray-400">
                            <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-20" />
                            <p>No jobs found. Check your database filter or connection.</p>
                        </div>
                    )}

                    {/* Table Header */}
                    {jobs.length > 0 && (
                        <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_minmax(11rem,0.5fr)_80px_40px] gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            <div>Job Title</div>
                            <div>Budget</div>
                            <div>Time</div>
                            <div className="text-center"></div>
                        </div>
                    )}

                    {/* Table Rows - Paginated */}
                    <div className="divide-y divide-gray-100 flex-1">
                        {jobs.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((job, index) => {
                            const isLastThree = index >= itemsPerPage - 3;
                            const jobKey = job.guid || job.id;
                            const isExpanded = expandedJobId === jobKey;

                            return (
                                <JobCard
                                    key={jobKey}
                                    job={job}
                                    isExpanded={isExpanded}
                                    isLastThree={isLastThree}
                                    onToggle={() => setExpandedJobId(isExpanded ? null : jobKey)}
                                    filters={filters}
                                />
                            );
                        })}
                    </div>

                    {/* Pagination Footer */}
                    {!loading && jobs.length > 0 && (
                        <PaginationFooter
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            totalJobs={jobs.length}
                            onChangeItemsPerPage={(size) => dispatchPagination({ type: 'SET_ITEMS_PER_PAGE', itemsPerPage: size })}
                            onPrevPage={() => dispatchPagination({ type: 'PREV_PAGE' })}
                            onNextPage={() => dispatchPagination({ type: 'NEXT_PAGE', totalPages: Math.ceil(jobs.length / itemsPerPage) })}
                        />
                    )}
                </div>

            </div>
        </div>
    );
}
